import { useCallback, useState } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  FieldWrapper,
  FloatingLabel,
  InfoRoundIconSmall,
  MessageTemplate,
  TagType,
  Text,
} from '@frontend/design-system';
import { ReviewLink } from './review-link';
import { ReviewLinkBanner } from './review-link-banner';

type ReviewsLandingTemplateProps = {
  handleMessageChange: (message: string) => void;
  helperTexts: { dynamicTagMessage?: string; requiredTagMessage?: string };
  initialTemplate: string;
  isFormModeEdit?: boolean;
  isTemplateTypeReview: boolean;
  mappedTags: TagType[];
  onReviewsLandingPageTemplateChange?: (reviewsLandingPageId: string) => void;
  reviewsLandingPageId?: string;
  saveToTemplateFormStore: () => void;
  templateLocations?: string[];
  variant: 'auto-message' | 'manual-template';
  hideTitle?: boolean;
};

const ReviewsLandingTemplate = ({
  handleMessageChange,
  helperTexts,
  initialTemplate,
  isFormModeEdit,
  isTemplateTypeReview,
  mappedTags,
  onReviewsLandingPageTemplateChange,
  reviewsLandingPageId,
  saveToTemplateFormStore,
  templateLocations,
  variant,
  hideTitle,
}: ReviewsLandingTemplateProps) => {
  const { t } = useTranslation('templates-settings');
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const [isLegacyReviewTemplate, setIsLegacyReviewTemplate] = useState<boolean>(false);

  const updateIsLegacyReviewTemplateState = useCallback((isLegacy: boolean) => {
    setIsLegacyReviewTemplate(isLegacy);
  }, []);

  const reviewLandingPageEditorFeatureFlag = getFeatureFlagValue('nwx:reviews-landing-page-editor', 'ANY');
  const isMediumScreen = useMatchMedia({ maxWidth: breakpoints.medium.min });

  return (
    <div>
      {isLegacyReviewTemplate && isTemplateTypeReview && <ReviewLinkBanner />}

      {!isFormModeEdit && !hideTitle && (
        <Text css={{ color: theme.colors.neutral50, marginBottom: '20px' }}>{t('Create your template message')}</Text>
      )}

      <MessageTemplate
        draggableTags
        hasDynamicTags
        initialTemplate={initialTemplate}
        isNewTag
        onChange={handleMessageChange}
        tags={mappedTags || []}
      >
        {isTemplateTypeReview && reviewLandingPageEditorFeatureFlag ? (
          <>
            <section css={messageAndLinkSectionStyles(isLegacyReviewTemplate, isMediumScreen)}>
              <FieldWrapper css={fieldWrapperMessageStyles(isMediumScreen)}>
                <FloatingLabel id={'review-template-message'} active>
                  {t('Your Message')}
                </FloatingLabel>
                <MessageTemplate.Editor
                  css={templateEditorStyles(true)}
                  placeholder={t('Customize your message')}
                  showEmojiPicker
                />
                <MessageTemplate.TagList showTagsOfType='REQUIRED' helperText={helperTexts.requiredTagMessage} />
                <MessageTemplate.TagList showTagsOfType='OPTIONAL' helperText={helperTexts.dynamicTagMessage} />
              </FieldWrapper>
              <FieldWrapper css={fieldWrapperPreviewLinkStyles(isMediumScreen)}>
                <FloatingLabel id={'review-template-link'} css={{ display: 'flex', gap: theme.spacing(0.5) }} active>
                  {t('Review Link')}
                  <InfoRoundIconSmall />
                </FloatingLabel>
                <ReviewLink
                  isLegacyReviewTemplate={isLegacyReviewTemplate}
                  onReviewsLandingPageTemplateChange={(reviewsLandignPageId) =>
                    onReviewsLandingPageTemplateChange?.(reviewsLandignPageId)
                  }
                  reviewsLandingPageId={reviewsLandingPageId}
                  saveToTemplateFormStore={saveToTemplateFormStore}
                  templateLocations={templateLocations}
                  updateIsLegacyReviewTemplateState={updateIsLegacyReviewTemplateState}
                  variant={variant}
                />
              </FieldWrapper>
            </section>
            <MessageTemplate.Preview
              css={{ backgroundColor: theme.colors.primary5, width: '100%', ...overrideTextStyles }}
            />
          </>
        ) : (
          <>
            <MessageTemplate.Editor
              css={templateEditorStyles(false)}
              placeholder={t('Customize your message')}
              showEmojiPicker
            />
            <MessageTemplate.TagList showTagsOfType='REQUIRED' helperText={helperTexts.requiredTagMessage} />
            <MessageTemplate.TagList showTagsOfType='OPTIONAL' helperText={helperTexts.dynamicTagMessage} />
            <MessageTemplate.Preview
              css={{ marginTop: theme.spacing(1), backgroundColor: theme.colors.primary5, ...overrideTextStyles }}
            />
          </>
        )}
      </MessageTemplate>
    </div>
  );
};

const messageAndLinkSectionStyles = (
  adjustPaddingForBanner: boolean,
  isMediumScreen: boolean
): Interpolation<Theme> => ({
  display: 'flex',
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(2),
  height: 'auto',
  marginTop: adjustPaddingForBanner ? theme.spacing(3) : 0,
  marginBottom: theme.spacing(2),
  flexDirection: isMediumScreen ? 'column' : 'row',
});

const templateEditorStyles = (isTemplateTypeReview: boolean): Interpolation<Theme> => ({
  marginBottom: theme.spacing(0.5),
  width: '100%',
  ...(isTemplateTypeReview && {
    '& [role="textbox"]': {
      height: '95px',
      minHeight: '95px',
      maxHeight: '95px',
      overflowY: 'scroll',
    },
  }),
});

const fieldWrapperMessageStyles = (isMediumScreen: boolean): Interpolation<Theme> => ({
  maxWidth: isMediumScreen ? '100%' : '66%',
  width: isMediumScreen ? '100%' : '66%',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  height: '100%',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: theme.spacing(1),
});

const fieldWrapperPreviewLinkStyles = (isMediumScreen: boolean): Interpolation<Theme> => ({
  maxWidth: isMediumScreen ? '205px' : '34%',
  width: isMediumScreen ? '205px' : '34%',
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  height: '360px',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
});

const overrideTextStyles = {
  fontWeight: 'normal',

  p: {
    whiteSpace: 'normal',
    span: {
      fontWeight: 'normal',
    },
  },
};

export { ReviewsLandingTemplate };
